// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import type { HexTriplet, StrongExtract } from '@evolvemep/api-ts';
import {
  Badge,
  type BadgeProps,
  type MantineSize,
  Stack,
  Progress,
  Flex,
  Text,
  type MantineColor,
} from '@mantine/core';

import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil } from 'helpers/isNotNil';

type Props = {
  label: string;
  color: MantineColor | HexTriplet;
  tooltip?: string;
  size?: StrongExtract<BadgeSize, 'lg' | 'xl' | 'xxl'>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  progress?: {
    value: number;
    outOf: number;
  };
} & Partial<BadgeProps>;

/** `xxl` is defined in `useGenerateMantineTheme` */
export type BadgeSize = MantineSize | 'xxl';

/**
 * Standard "Status" badge renderer.
 *
 * Used both standalone and via {@link EntityStatusBadge}.
 */
export const StatusBadge = ({ progress, size = 'lg', label, color, tooltip, onClick, ...props }: Props) => {
  const hasProgress = isNotNil(progress) && progress.outOf > 0;
  return (
    <AgGridStyleTooltip label={tooltip} hidden={!tooltip || tooltip === label} withArrow>
      <Badge
        tt="none"
        key={label}
        color={color}
        variant="filled"
        size={size}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        flex="0 0 auto"
        {...(size === 'xxl' && !hasProgress ? { fz: 'sm' } : {})}
        {...props}
        styles={{
          root: {
            justifyContent: 'space-between',
          },
          label: {
            textAlign: 'left',
            overflow: 'visible',
          },
          // TODO: `.style` can now be a function, need to handle it
          // oxlint-disable-next-line typescript/no-misused-spread
          ...props.styles,
        }}
      >
        <Stack gap={0}>
          <Flex align="center" justify="space-between" lh={1.14}>
            {label}
            {hasProgress && (
              <Text opacity={0.8}>
                {progress.value}/{progress.outOf}
              </Text>
            )}
          </Flex>
          {hasProgress && (
            <Progress value={100 * (progress.value / progress.outOf)} color="gray.0" size="xs" bg="gray.8" />
          )}
        </Stack>
      </Badge>
    </AgGridStyleTooltip>
  );
};
